import React, { useContext, useEffect, useState } from 'react';
import * as R from 'ramda';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import validator from 'validator';

import Button, { ButtonStyle } from '../../common/components/Button/Button';
import ButtonContainer from '../../common/components/Button/ButtonContainer';
import Container from '../../common/components/Container/Container';
import TitleContainer from '../../common/components/TitleContainer/TitleContainer';
import { EmailValidationContext } from '../../providers/EmailValidationContext';
import { Routes } from '../../config/routes';
import { EmailErrorType } from '../../providers/email/emailCapture';
import withRouteAnalytics from '../../services/analytics/withRouteAnalytics';
import { Screens } from '../../services/analytics/screens';

enum ErrorMessageI18n {
  invalidEmail = 'screens.email.capture.invalidEmail',
  network = 'screens.email.capture.networkError',
}

function EmailScreen(): JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState<boolean>(false);
  const [emailValid, setEmailValid] = useState<boolean>(false);
  const [showValidationError, setShowValidationError] = useState<boolean>(false);
  const [validationError, setValidationError] = useState<string>(ErrorMessageI18n.invalidEmail);
  const { email, setEmail, submitEmail } = useContext(EmailValidationContext);

  useEffect(() => {
    const emailIsValid = validator.isEmail(email);
    setEmailValid(emailIsValid);
    setShowValidationError(!emailIsValid && !R.isEmpty(email));
  }, [email]);

  const handleConfirm = async () => {
    setLoading(true);
    const result = await submitEmail();
    setLoading(false);
    if (result.isSuccessful()) {
      navigate(Routes.EMAIL_VERIFY);
      return;
    }
    const { errorType } = result.failure();
    switch (errorType) {
      case EmailErrorType.email:
        setEmailValid(false);
        setValidationError(ErrorMessageI18n.invalidEmail);
        setShowValidationError(true);
        break;
      case EmailErrorType.network:
        setValidationError(ErrorMessageI18n.network);
        setShowValidationError(true);
        break;
      default:
      case EmailErrorType.generic:
      case EmailErrorType.retriesExhausted:
        navigate(Routes.VALIDATION_REJECTED);
    }
  };

  return (
    <Container>
      <TitleContainer>
        <h1 data-testid="EMAIL_CAPTURE_HEADER">{t('screens.email.capture.title')}</h1>
      </TitleContainer>

      <div className="text-lg leading-6" data-testid="EMAIL_CAPTURE_DESCRIPTION">
        <p>{t('screens.email.capture.description')}</p>
      </div>

      <div className="text-lg leading-6 mt-12 mb-3">
        <input
          className={`w-72 rounded-md border-1 ring ring-transparent focus:ring-transparent bg-inputFieldBackground focus:bg-inputFieldBackgroundFocus ${
            showValidationError
              ? 'border-warning focus:border-warning'
              : 'border-inputFieldBorder focus:border-inputFieldBackgroundFocus'
          }`}
          type="text"
          placeholder={t('screens.email.capture.inputLabel')}
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          id="EMAIL_CAPTURE_INPUT"
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              handleConfirm();
            }
          }}
        />
      </div>
      <div className="mb-12" data-testid="EMAIL_INVALID">
        <>{showValidationError && <span className="textDetail text-warning">{t(validationError)}</span>}</>
        <>{!showValidationError && <div className="my-6" />}</>
      </div>

      <ButtonContainer>
        <Button
          style={ButtonStyle.DEFAULT}
          onClick={handleConfirm}
          disabled={!emailValid}
          loading={loading}
          dataTestid="CONFIRM_BUTTON"
        >
          <span>{t('screens.email.capture.buttonLabel')}</span>
        </Button>
      </ButtonContainer>
    </Container>
  );
}
export default withRouteAnalytics(EmailScreen, Screens.EMAIL_CAPTURE);
